import { useCallback, useMemo, useState } from 'react'
import { ProviderRoleId } from '../currentRole'
import { defaultLayoutForRole } from '../dashboard/defaultLayoutForRole'
import { LayoutDefinition } from '../dashboard/layout'
import { reconcileLayout } from '../dashboard/reconcileLayout'
import { PanelId } from '../dashboard/registry'
import { PanelSpan } from '../dashboard/spans'
import { advancedDelete, advancedGet, advancedPut } from './client'
import { makeLayoutControls } from './layoutControls'
import { useAdvancedResource } from './useAdvancedResource'

// The service answers `{ panels: null }` for a coach who has never changed their arrangement.
type LayoutResponse = { panels: unknown }

export type LayoutControls = {
  show: (index: number, panel: PanelId) => void
  resize: (index: number, span: PanelSpan) => void
  moveTo: (from: number, to: number) => void
  remove: (index: number) => void
  add: (panel: PanelId) => void
  addMany: (panels: PanelId[]) => void
  removeMany: (panels: PanelId[]) => void
  configure: (index: number, config: Record<string, unknown> | undefined) => void
  reset: () => void
}

export type DashboardLayoutState = {
  status: 'loading' | 'ready'
  layout: LayoutDefinition
  isCustom: boolean
  saveFailed: boolean
  controls: LayoutControls
}

const LAYOUT_PATH = '/dashboard-layout'

// Optimistic, never rolled back (`saveFailed` reports it); `role` only seeds a first-time layout.
export const useDashboardLayout = (role: ProviderRoleId): DashboardLayoutState => {
  const resource = useAdvancedResource<LayoutResponse>((signal) => advancedGet<LayoutResponse>(LAYOUT_PATH, signal), [])
  // `undefined` until this session edits or resets; `null` after a reset, so the fetched layout stays gone.
  const [local, setLocal] = useState<LayoutDefinition | null | undefined>(undefined)
  const [saveFailed, setSaveFailed] = useState(false)
  const fallback = defaultLayoutForRole(role)
  const savedPanels = resource.status === 'ready' ? resource.data.panels : null
  const saved = useMemo(
    () => (savedPanels === null ? null : reconcileLayout(savedPanels, fallback)),
    [savedPanels, fallback]
  )
  const current = local === undefined ? saved : local

  const status: 'loading' | 'ready' = resource.status === 'loading' ? 'loading' : 'ready'
  const layout = current ?? fallback

  const apply = useCallback((next: LayoutDefinition) => {
    setLocal(next)
    setSaveFailed(false)
    advancedPut<LayoutResponse>(LAYOUT_PATH, { panels: next.panels }).catch(() => setSaveFailed(true))
  }, [])

  const reset = useCallback(() => {
    setLocal(null)
    setSaveFailed(false)
    advancedDelete(LAYOUT_PATH).catch(() => setSaveFailed(true))
  }, [])

  const controls = useMemo<LayoutControls>(() => makeLayoutControls(layout, apply, reset), [apply, layout, reset])

  return { status, layout, isCustom: current !== null, saveFailed, controls }
}
